import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Tag } from 'lucide-react';
import { authHeaders } from '../../utils/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddressModal({
  address, datasetIds, onClose, donorFilter,
  unknownList, unknownIndex, onNavigate,
  canTag = false,
  onClusterTagged,
}) {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tagging, setTagging] = useState(null);

  // Prefetch cache shared across navigation steps. Key = "street|zip".
  // Value = { donations, total, totalPages }.
  const cacheRef = useRef(new Map());

  const buildParams = useCallback((addr, pageNum = 1) => {
    const params = new URLSearchParams({
      page: pageNum.toString(),
      page_size: '25',
      sort_by: 'contribution_amount',
      sort_order: 'desc',
    });
    if (addr.street && !donorFilter) {
      params.append('address', addr.street);
      if (addr.zip) params.append('address_zip', addr.zip);
    }
    if (donorFilter) params.append('donor', donorFilter);
    if (datasetIds) params.append('dataset_ids', datasetIds);
    return params;
  }, [donorFilter, datasetIds]);

  // Background prefetch — fills the cache without touching UI state.
  const prefetch = useCallback(async (addr) => {
    if (!addr) return;
    const key = `${addr.street}|${addr.zip}`;
    if (cacheRef.current.has(key)) return;
    try {
      const res = await fetch(`${API}/donations?${buildParams(addr, 1)}`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      cacheRef.current.set(key, {
        donations: data.data || [],
        total: data.total || 0,
        totalPages: data.total_pages || 1,
      });
    } catch (_) {
      /* swallow — best-effort prefetch */
    }
  }, [buildParams]);

  // Sync page state with the current address. Reading the previous address
  // during render and calling setState synchronously lets React discard the
  // current render and re-render with page=1, avoiding a stale first fetch
  // (which is what kept the Loading flash / cache miss around).
  const addrKey = `${address.street}|${address.zip}`;
  const [lastAddrKey, setLastAddrKey] = useState(addrKey);
  if (lastAddrKey !== addrKey) {
    setLastAddrKey(addrKey);
    setPage(1);
    setTagging(null);
  }

  const fetchData = useCallback(async () => {
    const key = `${address.street}|${address.zip}`;
    // Use the prefetch cache on page 1 — renders instantly, no flash.
    if (page === 1 && cacheRef.current.has(key)) {
      const cached = cacheRef.current.get(key);
      setDonations(cached.donations);
      setTotal(cached.total);
      setTotalPages(cached.totalPages);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/donations?${buildParams(address, page)}`, { headers: authHeaders() });
      const data = await res.json();
      setDonations(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      if (page === 1) {
        cacheRef.current.set(key, {
          donations: data.data || [],
          total: data.total || 0,
          totalPages: data.total_pages || 1,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [address.street, address.zip, page, buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Warm the cache for the neighbours (+/-2) so rapid Prev/Next scrolling is
  // instant. Bandwidth cost is small (~30-50 KB per cluster) and greatly
  // improves perceived responsiveness.
  useEffect(() => {
    if (!unknownList || typeof unknownIndex !== 'number') return;
    const neighbours = [
      unknownList[unknownIndex - 2],
      unknownList[unknownIndex - 1],
      unknownList[unknownIndex + 1],
      unknownList[unknownIndex + 2],
    ].filter(Boolean);
    neighbours.forEach(prefetch);
  }, [unknownList, unknownIndex, prefetch]);

  // Keyboard shortcuts for Prev/Next/First/Last when navigating a list
  useEffect(() => {
    if (!unknownList || typeof unknownIndex !== 'number' || !onNavigate) return;
    const handler = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && unknownIndex > 0) onNavigate(unknownIndex - 1);
      else if (e.key === 'ArrowRight' && unknownIndex < unknownList.length - 1) onNavigate(unknownIndex + 1);
      else if (e.key === 'Home' && unknownIndex !== 0) onNavigate(0);
      else if (e.key === 'End' && unknownIndex !== unknownList.length - 1) onNavigate(unknownList.length - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [unknownList, unknownIndex, onNavigate]);

  // Derive distinct committee names from the loaded donation rows so the user
  // can quickly tag them without leaving the modal.
  const committeeSummary = useMemo(() => {
    const map = new Map();
    for (const d of donations) {
      const name = (d.committee_name || '').trim();
      if (!name) continue;
      const key = name.toUpperCase();
      const prev = map.get(key) || { name: key, total: 0, count: 0 };
      prev.total += Number(d.contribution_amount || 0);
      prev.count += 1;
      map.set(key, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total).slice(0, 3);
  }, [donations]);

  const quickTag = async (committeeName, party) => {
    setTagging(`${committeeName}:${party}`);
    try {
      await axios.post(`${API}/party/tag-committee`, { committee_name: committeeName, party });
      const pretty = { republican: 'Republican', democrat: 'Democrat', skip: 'Skip' }[party];
      toast.success(`Tagged "${committeeName}" as ${pretty}`);
      window.dispatchEvent(new CustomEvent('party-tag-changed', { detail: { name: committeeName, party, action: 'tag' } }));
      // Tell the parent this cluster is done so it drops out of the navigator
      // list immediately (counter decrements for Rep/Dem AND Skip).
      onClusterTagged?.({ street: address.street, zip: address.zip });
      // Advance to the next unknown. Pick BY ADDRESS so we survive the list
      // shrinking — the next one stays the same cluster regardless of index
      // shifts caused by removals.
      if (unknownList && typeof unknownIndex === 'number' && onNavigate && unknownIndex < unknownList.length - 1) {
        onNavigate(unknownIndex + 1);
      } else if (unknownList && unknownIndex === unknownList.length - 1) {
        // Last one — close the modal gracefully
        onClose?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to tag');
    } finally {
      setTagging(null);
    }
  };

  const fmt = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const hasNav = unknownList && typeof unknownIndex === 'number' && onNavigate;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border rounded-sm shadow-2xl w-[90vw] max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="address-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b gap-3">
          <div className="min-w-0">
            <h2 className="font-heading font-semibold text-base truncate">
              {donorFilter ? donorFilter : address.address}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {donorFilter && <span>{address.address} &middot; </span>}
              {loading ? (address.donation_count ?? '…') : total} donations{!donorFilter && <> &middot; {address.unique_donors} donors &middot; {address.total_amount >= 1000 ? `$${(address.total_amount/1000).toFixed(1)}K` : `$${address.total_amount.toFixed(0)}`} total</>}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {hasNav && (
              <>
                <Button
                  variant="outline" size="sm" className="h-8 px-1.5"
                  disabled={unknownIndex <= 0}
                  onClick={() => onNavigate(0)}
                  data-testid="unknown-first-btn"
                  title="First unknown (Home)"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="sm" className="h-8 px-1.5"
                  disabled={unknownIndex <= 0}
                  onClick={() => onNavigate(unknownIndex - 1)}
                  data-testid="unknown-prev-btn"
                  title="Previous unknown (←)"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-muted-foreground font-medium px-1.5 whitespace-nowrap" data-testid="unknown-counter">
                  {unknownIndex + 1} / {unknownList.length}
                </span>
                <Button
                  variant="outline" size="sm" className="h-8 px-1.5"
                  disabled={unknownIndex >= unknownList.length - 1}
                  onClick={() => onNavigate(unknownIndex + 1)}
                  data-testid="unknown-next-btn"
                  title="Next unknown (→)"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="sm" className="h-8 px-1.5"
                  disabled={unknownIndex >= unknownList.length - 1}
                  onClick={() => onNavigate(unknownList.length - 1)}
                  data-testid="unknown-last-btn"
                  title="Last unknown (End)"
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-address-modal">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Quick-tag strip — shown for unknown clusters when the user has tag rights */}
        {canTag && hasNav && committeeSummary.length > 0 && (
          <div className="px-5 py-2.5 bg-amber-50/50 border-b" data-testid="quick-tag-strip">
            <div className="flex items-center gap-2 mb-1.5">
              <Tag className="w-3.5 h-3.5 text-amber-700" />
              <span className="text-xs font-medium text-amber-800">
                Unknown cluster — classify the committee(s) to fix this pin:
              </span>
            </div>
            <div className="space-y-1">
              {committeeSummary.map((c) => (
                <div key={c.name} className="flex items-center justify-between gap-2 text-xs" data-testid={`quick-tag-row-${c.name}`}>
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(`"${c.name}" FEC committee`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate flex-1 min-w-0 text-primary hover:underline hover:text-primary/80"
                    title={`Search: "${c.name}" on Google`}
                    data-testid={`search-committee-link-${c.name}`}
                  >
                    {c.name} <span className="text-muted-foreground no-underline">({c.count} &middot; ${c.total.toFixed(0)})</span>
                  </a>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      className="h-6 px-2 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                      disabled={tagging === `${c.name}:republican`}
                      onClick={() => quickTag(c.name, 'republican')}
                      data-testid={`quick-tag-rep-${c.name}`}
                    >
                      Rep
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className="h-6 px-2 text-[11px] border-blue-300 text-blue-700 hover:bg-blue-50"
                      disabled={tagging === `${c.name}:democrat`}
                      onClick={() => quickTag(c.name, 'democrat')}
                      data-testid={`quick-tag-dem-${c.name}`}
                    >
                      Dem
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className="h-6 px-2 text-[11px] border-slate-300 text-slate-600 hover:bg-slate-50"
                      disabled={tagging === `${c.name}:skip`}
                      onClick={() => quickTag(c.name, 'skip')}
                      title="Hide this committee from future audits (non-partisan / ambiguous)"
                      data-testid={`quick-tag-skip-${c.name}`}
                    >
                      Skip
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading && donations.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">Loading...</div>
          ) : (
            <table className={`w-full text-sm transition-opacity ${loading ? 'opacity-40' : ''}`}>
              <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-2.5 font-medium">Donor</th>
                  <th className="text-left px-4 py-2.5 font-medium">Recipient</th>
                  <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                  <th className="text-left px-4 py-2.5 font-medium">Date</th>
                  <th className="text-left px-4 py-2.5 font-medium">Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {donations.map((d, i) => (
                  <tr key={i} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2 font-medium">{d.contributor_name}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <span className={
                        (d.committee_name || '').includes('ACTBLUE') ? 'text-blue-600 font-medium' :
                        (d.committee_name || '').includes('WINRED') ? 'text-red-600 font-medium' : ''
                      }>
                        {d.committee_name}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{fmt(d.contribution_amount)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{d.contribution_date}</td>
                    <td className="px-4 py-2 text-muted-foreground text-xs">
                      {d.contributor_street_1 && `${d.contributor_street_1}, `}{d.contributor_city} {d.contributor_state} {d.contributor_zip}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t text-xs">
            <span className="text-muted-foreground">
              Page {page} of {totalPages} ({total} records)
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-3 h-3 mr-1" /> Prev
              </Button>
              <Button variant="outline" size="sm" className="h-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
